import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  resource,
  signal,
} from '@angular/core';

import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { handleRequestError } from '@softline/application';
import {
  AuthenticationContextStore,
  SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
} from '@softline/auth';
import {
  DateService,
  getIsoWeek,
  getYear,
  IsoDate,
  startOfWeek,
  Store,
} from '@softline/core';
import { combineLatest, filter, timer } from 'rxjs';
import { Timestamp, TimestampData } from '../../data/timestamp.model';
import { WorkHoursTargetService } from './work-hours-target.service';
import { StampCaptureService } from './work-hours.service';

import { KpiComponent, TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-work-hours-widget',
  templateUrl: './work-hours.widget.html',
  styleUrls: ['./work-hours.widget.css'],
  imports: [TranslatePipe, KpiComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkHoursWidget {
  readonly #service = inject(StampCaptureService);
  readonly #injector = inject(Injector);
  readonly #targetService = inject(WorkHoursTargetService);
  readonly dateService = inject(DateService);
  store = inject(Store);

  readonly dayTargetHours = this.#targetService.dayTargetHours;
  readonly weekTargetHours = this.#targetService.weekTargetHours;

  context = this.store.signal(
    SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
    AuthenticationContextStore.getters.data
  );

  readonly employeeId = computed(() => {
    return (this.context() as any)['personalid'];
  });

  readonly _dayData = resource({
    params: () => ({ employeeId: this.employeeId() }),
    loader: async (request) => {
      try {
        const date = this.dateService.now();
        const data = await this.#service.retrieve(
          date.substr(0, 10),
          date.substr(0, 10),
          request.params.employeeId
        );
        return data;
      } catch (error) {
        handleRequestError(error, this.#injector, {
          show: true,
        });
      }
      return null;
    },
  });

  readonly _weekData = resource({
    params: () => ({ employeeId: this.employeeId() }),
    loader: async (param) => {
      try {
        const date = this.dateService.now();
        const sow = startOfWeek(date);
        const data = await this.#service.retrieve(
          sow.substr(0, 10),
          date.substr(0, 10),
          param.params.employeeId
        );
        return data;
      } catch (error) {
        handleRequestError(error, this.#injector, {
          show: true,
        });
      }
      return null;
    },
  });

  readonly weekData = computed(() => {
    return this._weekData.value() ?? undefined;
  });

  readonly dayData = computed(() => {
    return this._dayData.value() ?? undefined;
  });

  readonly dayData$ = toObservable(this.dayData);

  readonly now = signal(Date.now());

  constructor() {
    combineLatest([timer(0, 1000), toObservable(this.#service.isStamped)])
      .pipe(
        takeUntilDestroyed(),
        filter(([_, isStamped]) => isStamped)
      )
      .subscribe(() => {
        this.now.set(Date.now());
      });
  }

  #calculateWorkHours = (entries: Timestamp[] | undefined) => {
    if (!entries) return;
    const currentTime = this.now();
    let totalMilliseconds = 0;
    let currentArrival: number | null = null;
    for (const entry of entries) {
      const time = new Date(entry.timestamp).getTime();
      if (entry.flag === 'arrival') {
        currentArrival = time;
      } else if (entry.flag === 'departure' && currentArrival !== null) {
        totalMilliseconds += time - currentArrival;
        currentArrival = null;
      }
    }
    if (currentArrival !== null) {
      totalMilliseconds += currentTime - currentArrival;
    }
    const totalSeconds = Math.floor(totalMilliseconds / 1000);
    const seconds = totalSeconds % 60;
    const totalMinutes = Math.floor(totalSeconds / 60);
    const minutes = totalMinutes % 60;
    const hours = Math.floor(totalMinutes / 60);

    return {
      hours: this.pad(hours),
      minutes: this.pad(minutes),
      seconds: this.pad(seconds),
    };
  };

  #calculatePauseTime(entries: Timestamp[] = []): {
    hours: string;
    minutes: string;
    seconds: string;
  } {
    let totalMilliseconds = 0;
    let currentDeparture: number | null = null;

    for (const entry of entries) {
      const time = new Date(entry.timestamp).getTime();

      if (entry.flag === 'departure') {
        currentDeparture = time;
      } else if (entry.flag === 'arrival' && currentDeparture !== null) {
        // Time between departure → arrival is pause
        totalMilliseconds += time - currentDeparture;
        currentDeparture = null;
      }
    }

    const totalSeconds = Math.floor(totalMilliseconds / 1000);
    const seconds = totalSeconds % 60;
    const totalMinutes = Math.floor(totalSeconds / 60);
    const minutes = totalMinutes % 60;
    const hours = Math.floor(totalMinutes / 60);

    return {
      hours: this.pad(hours),
      minutes: this.pad(minutes),
      seconds: this.pad(seconds),
    };
  }

  sortByDate(a: Timestamp, b: Timestamp) {
    const dateA = new Date(a.timestamp);
    const dateB = new Date(b.timestamp);
    if (dateA < dateB) {
      return -1;
    }
    if (dateA > dateB) {
      return 1;
    }
    return 0;
  }

  sameDay(d1: string, d2: string) {
    const date1 = new Date(d1);
    const date2 = new Date(d2);
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  sameWeek(d1: string, d2: string) {
    const date1 = new Date(d1).toISOString();
    const date2 = new Date(d2).toISOString();
    return (
      getYear(date1 as IsoDate) === getYear(date2 as IsoDate) &&
      getIsoWeek(date1 as IsoDate) === getIsoWeek(date2 as IsoDate)
    );
  }

  readonly dayWorkHoursPercentage = computed(() => {
    const workHours = this.dayWorkHours();
    if (!workHours) return 0;
    const targetHours = this.dayTargetHours();
    if (targetHours <= 0) return 100;
    const hours = parseInt(workHours.hours ?? '0', 10);
    const minutes = parseInt(workHours.minutes ?? '0', 10);
    const totalHours = hours + minutes / 60;
    return Math.min(100, (totalHours / targetHours) * 100);
  });

  readonly weekWorkHoursPercentage = computed(() => {
    const workHours = this.weekWorkHours();
    if (!workHours) return 0;
    const targetHours = this.weekTargetHours();
    if (targetHours <= 0) return 100;
    const hours = parseInt(workHours.hours ?? '0', 10);
    const minutes = parseInt(workHours.minutes ?? '0', 10);
    const totalHours = hours + minutes / 60;
    return Math.min(100, (totalHours / targetHours) * 100);
  });

  readonly dayWorkHours = computed(() => {
    const data = this.dayData() as TimestampData;
    if (!data) return;
    const date = this.dateService.now();
    data.result = data.result.filter((entry: Timestamp) => {
      return this.sameDay(entry.timestamp, date);
    });
    data.result = data.result.sort((a: Timestamp, b: Timestamp) => {
      return this.sortByDate(a, b);
    });
    return this.#calculateWorkHours(data.result as Timestamp[]);
  });

  readonly weekWorkHours = computed(() => {
    const data = this.weekData() as TimestampData;
    if (!data) return;
    const date = this.dateService.now();
    // filter out other dates
    data.result = data.result.filter((entry: Timestamp) => {
      return this.sameWeek(entry.timestamp, date);
    });

    // sort by date
    data.result = data.result.sort((a: Timestamp, b: Timestamp) => {
      return this.sortByDate(a, b);
    });

    return this.#calculateWorkHours(data.result as Timestamp[]);
  });

  readonly pauseTime = computed(() => {
    const data = this.dayData() as TimestampData;
    if (!data) return;
    return this.#calculatePauseTime(data.result as Timestamp[]);
  });

  readonly dayWorkDecimal = computed(() => this.toDecimalHours(this.dayWorkHours()));
  readonly weekWorkDecimal = computed(() => this.toDecimalHours(this.weekWorkHours()));
  readonly pauseDecimal = computed(() => this.toDecimalHours(this.pauseTime()));

  readonly formatHms = (value: number): string => {
    const totalSeconds = Math.max(0, Math.floor(value * 3600));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${this.pad(h)}:${this.pad(m)}:${this.pad(s)}`;
  };

  readonly formatHm = (value: number): string => {
    const totalSeconds = Math.max(0, Math.floor(value * 3600));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    return `${this.pad(h)}:${this.pad(m)}`;
  };

  readonly formatHourDelta = (
    _deltaPercent: number,
    value: number,
    target: number
  ): string => {
    const diff = value - target;
    const sign = diff >= 0 ? '+' : '-';
    const abs = Math.abs(diff);
    const totalMinutes = Math.round(abs * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${sign}${h}:${this.pad(m)} Std`;
  };

  private toDecimalHours(
    t: { hours: string; minutes: string; seconds: string } | undefined
  ): number {
    if (!t) return 0;
    const h = parseInt(t.hours ?? '0', 10);
    const m = parseInt(t.minutes ?? '0', 10);
    const s = parseInt(t.seconds ?? '0', 10);
    return h + m / 60 + s / 3600;
  }

  pad(n: number | undefined | null): string {
    if (n === undefined || n === null) return '00';
    return n.toString().padStart(2, '0');
  }
}
